import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import certifiedTrainerService from '../../services/certifiedTrainer';
import { Enrollment, Course } from '../../types';
import { useToast } from '../../hooks/useToast';
import ExpiryCell from '../../components/common/ExpiryCell';
import ExtendExpiryModal from '../../components/common/ExtendExpiryModal';

export default function TeachingStudents() {
  const { t } = useTranslation('teaching');
  const { courseId } = useParams();
  const toast = useToast();
  const [course, setCourse] = useState<Course | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'active' | 'completed'>('all');
  const [processing, setProcessing] = useState<string | null>(null);
  const [expiryTarget, setExpiryTarget] = useState<Enrollment | null>(null);

  useEffect(() => {
    loadData();
  }, [courseId]);

  async function loadData() {
    try {
      const [coursesData, studentsData] = await Promise.all([
        certifiedTrainerService.getCourses(),
        certifiedTrainerService.getCourseStudents(courseId!),
      ]);
      const foundCourse = coursesData.find((c) => c._id === courseId);
      setCourse(foundCourse || null);
      setEnrollments(studentsData);
    } catch (err: any) {
      setError(err.response?.data?.error || t('students.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  }

  async function handleEnrollmentAction(enrollmentId: string, action: 'approve' | 'reject') {
    setProcessing(enrollmentId);
    try {
      await certifiedTrainerService.updateEnrollment(enrollmentId, action);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || (action === 'approve' ? t('students.failedToApprove') : t('students.failedToReject')));
    } finally {
      setProcessing(null);
    }
  }

  async function handleSetExpiry(expiresAt: string | null) {
    if (!expiryTarget) return;
    await certifiedTrainerService.setEnrollmentExpiry(expiryTarget._id, expiresAt);
    await loadData();
  }

  const filteredEnrollments = enrollments.filter((e) =>
    filter === 'all' ? true : e.status === filter
  );

  const statusCounts = {
    all: enrollments.length,
    pending: enrollments.filter((e) => e.status === 'pending').length,
    active: enrollments.filter((e) => e.status === 'active').length,
    completed: enrollments.filter((e) => e.status === 'completed').length,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-red-50 text-red-600 p-4 rounded-md">{t('students.courseNotFound')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="mb-8">
        <Link
          to="/my-teaching/courses"
          className="text-gold hover:underline text-sm mb-2 inline-block"
        >
          &larr; {t('students.backToCourses')}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">{course.title}</h1>
        <p className="text-gray-500 mt-1">{t('students.subtitle')}</p>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6">{error}</div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="glass-card rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-900">{statusCounts.all}</div>
          <div className="text-gray-500 text-sm">{t('students.totalEnrollments')}</div>
        </div>
        <div className="glass-card rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-400">{statusCounts.pending}</div>
          <div className="text-gray-500 text-sm">{t('students.pendingApproval')}</div>
        </div>
        <div className="glass-card rounded-lg p-4">
          <div className="text-2xl font-bold text-gold">{statusCounts.active}</div>
          <div className="text-gray-500 text-sm">{t('students.activeMembers')}</div>
        </div>
        <div className="glass-card rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{statusCounts.completed}</div>
          <div className="text-gray-500 text-sm">{t('students.completed')}</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {(['all', 'pending', 'active', 'completed'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 -mb-px ${
              filter === status
                ? 'border-b-2 border-gold text-gold font-medium'
                : 'text-gray-500 hover:text-gray-600'
            }`}
          >
            {t(`students.filter${status.charAt(0).toUpperCase() + status.slice(1)}`)}
            {status === 'pending' && statusCounts.pending > 0 && (
              <span className="ml-2 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs">
                {statusCounts.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {filteredEnrollments.length === 0 ? (
        <div className="text-center py-12 glass-card rounded-lg">
          <p className="text-gray-500">
            {filter === 'all'
              ? t('students.noMembersYet')
              : t('students.noFilteredEnrollments', { filter })}
          </p>
        </div>
      ) : (
        <div className="glass-card rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('students.memberColumn')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('students.enrolledColumn')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('students.progressColumn')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('students.statusColumn')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('students.accessColumn')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('students.actionsColumn')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEnrollments.map((enrollment) => (
                <tr key={enrollment._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {enrollment.user?.avatar ? (
                        <img
                          src={enrollment.user.avatar}
                          alt=""
                          className="h-10 w-10 rounded-full"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 text-sm">
                            {enrollment.user?.name?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {enrollment.user?.name || t('students.unknown')}
                        </div>
                        <div className="text-sm text-gray-500">
                          {enrollment.user?.email || ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(enrollment.enrolledAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gold"
                          style={{ width: `${enrollment.progress}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-500">{enrollment.progress}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        enrollment.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : enrollment.status === 'active'
                          ? 'bg-gold/20 text-gold'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {enrollment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ExpiryCell expiresAt={enrollment.expiresAt} isExpired={enrollment.isExpired} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end items-center gap-3">
                      <button
                        onClick={() => setExpiryTarget(enrollment)}
                        className="text-gold hover:underline"
                      >
                        {t('students.extendAccess')}
                      </button>
                    {enrollment.status === 'pending' && (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEnrollmentAction(enrollment._id, 'approve')}
                          disabled={processing === enrollment._id}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                        >
                          {t('students.approve')}
                        </button>
                        <button
                          onClick={() => handleEnrollmentAction(enrollment._id, 'reject')}
                          disabled={processing === enrollment._id}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          {t('students.reject')}
                        </button>
                      </div>
                    )}
                    {enrollment.status === 'completed' && (
                      <span className="text-gray-500">{t('students.completedStatus')}</span>
                    )}
                    {enrollment.status === 'active' && enrollment.message && (
                      <span
                        className="text-gray-500 cursor-help"
                        title={enrollment.message}
                      >
                        {t('students.viewMessage')}
                      </span>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ExtendExpiryModal
        isOpen={!!expiryTarget}
        onClose={() => setExpiryTarget(null)}
        subject={expiryTarget?.user?.name || undefined}
        currentExpiresAt={expiryTarget?.expiresAt}
        onSubmit={handleSetExpiry}
      />
    </div>
  );
}
